import TagManager from "react-gtm-module"

const GTM_ID = process.env.REACT_APP_GTM || "GTM-5NCMCC8"
const APP_ROOT = "/accountmanagement"

/**
 * Initializes Google TagManager. Google Analytics account number is set in 
 * TagManager lookup table variable based on environment
 */
export const setGtm = () => {
  // gtmId is the Google TagManager account number
  const tagManagerArgs = {
    gtmId: GTM_ID,
  }

  /*
    Initialize tagManager if GTM account is set and not in the test environment; 
    build pipeline will fail if GTM is initialized when running units tests
  */
  if (tagManagerArgs && process.env.NODE_ENV !== "test") {
    TagManager.initialize(tagManagerArgs)

    TagManager.dataLayer({
      dataLayer: {
        appName: "PCC-Acct",
      }
    })
  }

  // record initial page view
  recordPageView()
}

/**
 * Returns a path string with a root string identifying the application prepended 
 * 
 * @param {string} path path used for pageview
 * 
 * @returns {string} path with APP_ROOT constant string prepended
 */
export const createPathString = (path: string) => {
  return APP_ROOT + path
}

/**
 * Causes a "Record Page View" event to be recorded in GA with the provided path
 */
export const recordPageView = () => {
  TagManager.dataLayer({
    dataLayer: {
      event: "Record Page View - Account",
    }
  })
}

/**
 * Causes a "Record Page View" event to be recorded in GA with the provided path
 * 
 * @param {string} path path used for pageview; prepended with APP_ROOT path constant
 */
export const recordVirtualView = (path: string) => {
  TagManager.dataLayer({
    dataLayer: {
      usePath: path,
      event: "Record Virtual View - Account",
    }
  })
}

/**
 * Causes a "Record Link Action" event to be recorded in GA with its label set
 * to "ID: affectedId".
 * 
 * @param {string} affectedId key of entity affected by this action
 * @param {string} [description] optional additional info prepended to affectedId
 * @param {string} [newPath] optional path overrides existing path
 */
export const recordEvent = (action: string, affectedId: string, description: string = "",
  newPath?: string) => {

  // override path if newPath provided
  if (newPath) {
    TagManager.dataLayer({
      dataLayer: {
        usePath: newPath,
      }
    })
  }

  TagManager.dataLayer({
    dataLayer: {
      capturedText: action,
      description: description,
      affectedId: affectedId,
      event: "Record Link Action",
    }
  })
}

/**
 * Causes a "Record Search Text" event to be recorded in GA with its label set
 * to the search string entered by user.
 * 
 * @param {string} capturedText search string entered by user
 * @param {string} [newPath] optional path overrides existing path
 */
export const recordSearchTerm = (capturedText: string, newPath?: string) => {
  // override path if newPath provided
  if (newPath) {
    TagManager.dataLayer({
      dataLayer: {
        usePath: newPath,
      }
    })
  }

  TagManager.dataLayer({
    dataLayer: {
      capturedText: capturedText,
      event: "Record Search Text",
    }
  })

  // clear variable after event recorded
  TagManager.dataLayer({
    dataLayer: {
      capturedText: undefined,
    }
  })
}

/**
 * Causes a "Record Checkbox Checked" or "Record Checkbox Unchecked" event to be recorded in GA
 * depending on the currect checkbox value.
 * 
 * @param {boolean} isChecked boolean describing whether checkbox is currently checked or unchecked
 * @param {string} description description of checkbox function
 * @param {string} [newPath] optional path overrides existing path
 */
export const recordCheckbox = (isChecked: boolean, description: string, newPath?: string) => {
  // override path if newPath provided
  if (newPath) {
    TagManager.dataLayer({
      dataLayer: {
        usePath: newPath,
      }
    })
  }

  TagManager.dataLayer({
    dataLayer: {
      capturedText: description,
      event: isChecked ? "Record Checkbox Checked" : "Record Checkbox Unchecked",
    }
  })
}

/**
 * Determines the correct path format to record Google Analytics page view with 
 * depending on pages context and whether id's need to be stripped from it
 * 
 * @param {string} pathURL The url path string to alter.
 * @param {string} beginString The starting left-hand side of pathURL to keep.
 * @param {string} endString A delimiter string indicating the end of the substring to delete.
 * 
 * @returns {string} path string with id stripped out
 */
export const getStrippedPath = (pathURL: string, beginString: string, endString: string) => {
  // strip out root path if pathURL includes it
  if (pathURL.startsWith(APP_ROOT)) {
    pathURL = pathURL.replace(APP_ROOT, "")
    if (!pathURL) {
      pathURL = "/"
    }
  }

  const gaPath = stripFromURLPath(pathURL, beginString, endString)

  // determine type of page view
  const accountRootPage = (gaPath.strippedString && gaPath.newPath === '/')
  const noAccountSpecified = (!gaPath.strippedString && gaPath.newPath === '/')
  const pageURLWithId = (gaPath.strippedString && gaPath.newPath !== '/')
  const pageURLWithoutId = (!gaPath.strippedString && gaPath.newPath !== '/')

  let pathString = ""

  if (noAccountSpecified) {
    TagManager.dataLayer({
      dataLayer: {
        marketSegment: undefined,
      }
    })
    pathString = "/preferred-account"
  }
  else if (accountRootPage) pathString = "/account-selection"
  else if (pageURLWithId) pathString = null
  else if (pageURLWithoutId) pathString = null

  return pathString
}

/**
 * This function takes a URL path string and strips out an unwanted substring from it. 
 * Both the altered path string and the removed substring are returned.  
 * 
 * The intention of the function is to be used to form more generic Google Analytics 
 * page view hit paths by stripping out id substrings from the URL.  This allows for 
 * better grouping of page hits data.  Because the stripped out substring is also returned, 
 * this data does not need to be discarded; if desired, it can be passed along with 
 * the page hit by setting it to a custom dimension 
 * 
 * @param {string} pathURL      The url path string to alter.
 * @param {string} beginString  The starting left-hand side of pathURL to keep.
 * @param {string} endString    A delimiter string indicating the end of the 
 *                              substring to delete.
 * 
 * @return {Object} objectVar newPath: a url path with the desired substring removed
 *  strippedString: the removed substring; returns "" if operation fails.
 * 
 * usage example:
 * given the path string: "/root_path/string_to_strip_out/end_of_path/"
 * to strip out the substring "string_to_strip/" and create a new path "/root_path/end_of_path/"
 * beginString is set to "/root_path/" and endString is "/"
 * the function keeps the beginString portion, then searches for the first occurence of 
 * endString "/" occuring after beginString.  Everything occurring after this first occurrence 
 * of endString is also kept. What lies in between beginString and endString 
 * is stripped out/deleted (in this case, "string_to_strip/").
 * If no occurrance of endString is detected after the beginString portion, this indicates the
 * substring to remove occurs at the end of the path string; everything
 * after beginString will be stripped out
 */
export const stripFromURLPath = (pathURL: string, beginString: string, endString: string) => {
  let newPath = beginString
  let strippedString = ''

  // perform function only if parameters are not null and the URL path
  // starts with the beginString; otherwise, return original path and an
  // empty strippedString property
  if (pathURL && beginString && endString && pathURL.startsWith(beginString)) {

    // calculate the starting and ending index positions to strip out from
    // the pathURL string:

    // the strip starts at the position immediately following the beginString and
    // ends at the index of the first occurrence of the endString;
    // if endString is not found, everything after beginString is stripped out
    let i = pathURL.indexOf(endString, beginString.length + 1)

    // the below condition is met when a match for indexOf() is found
    if (i !== -1) {
      // create new URL path with characters between beginString and endString
      // stripped out
      newPath += pathURL.substring(i + 1)

      // portion to remove occurs at between beginString 
      // and the next occurrance of endString (inclusive)
      strippedString = pathURL.slice(beginString.length, i)

      return { newPath: newPath, strippedString: strippedString }
    } else {
      // portion to remove occurs at end of pathURL;
      // strip out everything after beginString
      strippedString = pathURL.substring(beginString.length)

      return { newPath: newPath, strippedString: strippedString }
    }
  }
  // pathURL does not start with beginString, original path returned; 
  // nothing stripped out
  return { newPath: pathURL, strippedString: strippedString }
}